import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonicModule, ModalController, PopoverController } from '@ionic/angular';
import moment from 'moment';

import { Escalation, PriorityTokens } from '../../../models';
import { ColonToEmojiPipe } from '../../../pipes/colonToEmoji.pipe';
import { SharedPipesModule } from '../../../pipes/shared-pipes.module';
import { AuthService } from '../../../services/auth.service';
import { EscalationsService } from '../../../services/escalations.service';
import { MessageService } from '../../../services/message.service';
import { SearchablePopoverService } from '../../../services/searchable-popover.service';
import { TokenService } from '../../../services/token.service';
import {
  CONVERSATION_PRIORITY_VALUES,
  DEFAULT_PRIORITY_TOKENS,
  ESCALATION_STATUSES,
  ESCALATION_STATUS_LABELS,
  SUPPORT_STEP_ASSIGNEE_TYPES,
} from '../../constants';
import {
  createPriorityValueToTokenMap,
  getColorForConversationPriority,
  getIconForConversationPriority,
  isUrl,
  getDisplayTextForDuration,
} from '../../utils/utils';
import { ActionDropdownComponent, ActionDropdownType } from '../action-dropdown/action-dropdown.component';
import { AvatarModule } from '../avatar/avatar.module';

interface TimelineEvent {
  type: string;
  timestamp: Date;
  data: any;
}

@Component({
  selector: 'app-escalation-detail',
  templateUrl: './escalation-detail.component.html',
  styleUrls: ['./escalation-detail.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonicModule,
    AvatarModule,
    ActionDropdownComponent,
    ColonToEmojiPipe,
    SharedPipesModule,
  ],
})
export class EscalationDetailComponent implements OnInit {
  @Input() escalation: Escalation;

  isLoading = false;
  timelineEvents: TimelineEvent[] = [];
  priorityTokens: Record<string, PriorityTokens> = DEFAULT_PRIORITY_TOKENS;
  priorityValueToTokenMap: Record<number, PriorityTokens> = createPriorityValueToTokenMap(DEFAULT_PRIORITY_TOKENS);

  // Constants
  readonly ESCALATION_STATUSES = ESCALATION_STATUSES;
  readonly ESCALATION_STATUS_LABELS = ESCALATION_STATUS_LABELS;
  readonly CONVERSATION_PRIORITY_VALUES = CONVERSATION_PRIORITY_VALUES;

  // Helper function for template
  readonly isUrl = isUrl;

  constructor(
    private modalCtrl: ModalController,
    private popoverCtrl: PopoverController,
    private escalationsService: EscalationsService,
    private searchablePopoverService: SearchablePopoverService,
    private msgService: MessageService,
    private tokenService: TokenService,
    private authService: AuthService,
  ) {}

  async ngOnInit() {
    await this.initializePriorityTokens();
    this.buildTimelineEvents();
  }

  async initializePriorityTokens() {
    this.priorityTokens = (
      await this.tokenService.getResolvedTokens(['PRIORITY_LABEL_MAP'], this.escalation.conversationId)
    )['PRIORITY_LABEL_MAP'];
    this.priorityValueToTokenMap = createPriorityValueToTokenMap(this.priorityTokens);
  }

  buildTimelineEvents(): void {
    const events: TimelineEvent[] = [];

    // Add creation event
    events.push({
      type: 'created',
      timestamp: new Date(this.escalation.createdAt),
      data: {
        label: 'Escalation created',
        description: `Created by ${this.escalation.escalationType?.name || 'Unknown type'}`,
      },
    });

    // Add acknowledged event if acknowledgedAt exists
    if (this.escalation.acknowledgedAt) {
      const timeToAcknowledgement = this.escalation.escalationToAcknowledgementDuration;
      const timeToAcknowledgementWorking = this.escalation.escalationToAcknowledgementDurationWorking;
      const durationText = getDisplayTextForDuration(timeToAcknowledgement);
      const durationTextWorking = getDisplayTextForDuration(timeToAcknowledgementWorking);

      events.push({
        type: 'acknowledged',
        timestamp: new Date(this.escalation.acknowledgedAt),
        data: {
          label: 'Acknowledged',
          description: `Acknowledged after ${durationText} (${durationTextWorking} working time)`,
        },
      });
    }

    if (this.escalation.resolvedAt && this.escalation.status === ESCALATION_STATUSES.RESOLVED) {
      const timeToResolution = this.escalation.acknowledgementToResolutionDuration;
      const timeToResolutionWorking = this.escalation.acknowledgementToResolutionDurationWorking;
      const durationText = getDisplayTextForDuration(timeToResolution);
      const durationTextWorking = getDisplayTextForDuration(timeToResolutionWorking);

      events.push({
        type: 'resolved',
        timestamp: new Date(this.escalation.resolvedAt),
        data: {
          label: 'Resolved',
          description: `Resolved ${durationText} after acknowledgement (${durationTextWorking} working time)`,
        },
      });
    }

    // Sort by timestamp
    this.timelineEvents = events.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  }

  dismiss() {
    this.modalCtrl.dismiss();
  }

  displayDateFrom(date: Date): string {
    return moment(date).fromNow();
  }

  displayPriority(): string {
    return this.priorityValueToTokenMap[this.escalation?.priority]?.LABEL || 'None';
  }

  displayPriorityEmoji(priority: number) {
    return priority ? this.priorityValueToTokenMap[priority]?.EMOJI || this.getIconForPriority(priority) : null;
  }

  getIconForPriority(priority: number) {
    return getIconForConversationPriority(priority);
  }

  getColorForPriority(priority: number) {
    return getColorForConversationPriority(priority);
  }

  async changeEscalationStatus(event: Event) {
    this.searchablePopoverService.escalationStatuses({
      event,
      selectedStatusId: this.escalation.status,
      callback: async (id: string) => {
        if (id && id !== this.escalation.status) {
          await this.updateEscalation({ status: id });
        }
      },
    });
  }

  async changeEscalationPriority(event: Event) {
    const options: ActionDropdownType[] = [
      {
        label: this.priorityTokens.LOW.LABEL,
        emoji: this.priorityTokens.LOW.EMOJI,
        icon: this.getIconForPriority(CONVERSATION_PRIORITY_VALUES.LOW),
      },
      {
        label: this.priorityTokens.MEDIUM.LABEL,
        emoji: this.priorityTokens.MEDIUM.EMOJI,
        icon: this.getIconForPriority(CONVERSATION_PRIORITY_VALUES.MEDIUM),
      },
      {
        label: this.priorityTokens.HIGH.LABEL,
        emoji: this.priorityTokens.HIGH.EMOJI,
        icon: this.getIconForPriority(CONVERSATION_PRIORITY_VALUES.HIGH),
      },
      {
        label: this.priorityTokens.CRITICAL.LABEL,
        emoji: this.priorityTokens.CRITICAL.EMOJI,
        icon: this.getIconForPriority(CONVERSATION_PRIORITY_VALUES.CRITICAL),
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: async (idx: number) => {
          popover.dismiss();

          let priority: number | null;

          switch (idx) {
            case 0:
              priority = CONVERSATION_PRIORITY_VALUES.LOW;
              break;
            case 1:
              priority = CONVERSATION_PRIORITY_VALUES.MEDIUM;
              break;
            case 2:
              priority = CONVERSATION_PRIORITY_VALUES.HIGH;
              break;
            case 3:
              priority = CONVERSATION_PRIORITY_VALUES.CRITICAL;
              break;
          }

          if (priority !== this.escalation.priority) {
            await this.updateEscalation({ priority });
          }
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  async changeEscalationAssignee(event: Event) {
    this.searchablePopoverService.assignees({
      event,
      selectedId: this.escalation.assignedToGroupId ?? this.escalation.assignedToUserId,
      showPrimaryRepOption: false,
      showCreatedByUserOption: false,
      includeNonAgents: true,
      showNoneOption: true,
      includeGroups: true,
      callback: async (id: string, name: string, object: any) => {
        if (object.type === SUPPORT_STEP_ASSIGNEE_TYPES.USER || !id) {
          await this.updateEscalation({
            assignedToUserId: id,
            assignedToGroupId: null,
          });
        } else if (object.type === SUPPORT_STEP_ASSIGNEE_TYPES.TEAM) {
          const selectedGroupId = id;
          await this.promptForUserInGroup(selectedGroupId, event);
        }
      },
    });
  }

  async promptForUserInGroup(groupId: string, event: Event) {
    this.searchablePopoverService.assignees({
      event,
      selectedId: this.escalation.assignedToGroupId === groupId ? this.escalation.assignedToUserId : undefined,
      showAutoAssignFromGroupOption: true,
      showNoneOption: true,
      includeNonAgents: true,
      includeGroups: false,
      showPrimaryRepOption: false,
      showCreatedByUserOption: false,
      userGroupId: groupId,
      callback: async (selectedUserId: string, name: string, object: any) => {
        await this.updateEscalation({
          assignedToUserId: selectedUserId,
          assignedToGroupId: groupId,
        });
      },
    });
  }

  async updateEscalation(changes: Partial<Escalation>) {
    try {
      this.isLoading = true;
      const updatedEscalation = await this.escalationsService.updatePartial(this.escalation.id, changes);

      // Update the local escalation object
      this.escalation = { ...this.escalation, ...updatedEscalation };

      // Rebuild timeline events
      this.buildTimelineEvents();

      this.msgService.show('✔︎ Escalation updated', { duration: 2000, position: 'bottom' });
    } catch (error) {
      this.msgService.showError(error);
    } finally {
      this.isLoading = false;
    }
  }

  openTriageInSlack(messageTs: string, channelId: string) {
    // This would typically open the Slack message in a new window
    // For now, we'll just show a message
    this.msgService.show('Opening in Slack...');
  }

  getStatusIcon(status: string): string {
    switch (status) {
      case ESCALATION_STATUSES.PENDING:
        return 'custom-open';
      case ESCALATION_STATUSES.ACKNOWLEDGED:
        return 'custom-in-progress';
      case ESCALATION_STATUSES.RESOLVED:
        return 'custom-closed';
      default:
        return 'help-circle';
    }
  }

  getStatusColor(status: string): string {
    switch (status) {
      case ESCALATION_STATUSES.PENDING:
        return 'danger';
      case ESCALATION_STATUSES.ACKNOWLEDGED:
        return 'warning';
      case ESCALATION_STATUSES.RESOLVED:
        return 'success';
      default:
        return 'medium';
    }
  }

  getStatusGradientColors(status: string): { from: string; to: string } {
    switch (status) {
      case ESCALATION_STATUSES.PENDING:
        return { from: 'rgba(235, 68, 90, 0.08)', to: 'rgba(235, 68, 90, 0.02)' };
      case ESCALATION_STATUSES.ACKNOWLEDGED:
        return { from: 'rgba(255, 196, 0, 0.08)', to: 'rgba(255, 196, 0, 0.02)' };
      case ESCALATION_STATUSES.RESOLVED:
        return { from: 'rgba(45, 211, 111, 0.08)', to: 'rgba(45, 211, 111, 0.02)' };
      default:
        return { from: 'rgba(116, 130, 141, 0.08)', to: 'rgba(116, 130, 141, 0.02)' };
    }
  }

  getTimelineIcon(eventType: string): string {
    switch (eventType) {
      case 'created':
        return 'add-circle';
      case 'acknowledged':
        return 'checkmark-circle';
      case 'resolved':
        return 'checkmark-done-circle';
      case 'reopened':
        return 'pulse';
      case 'assigned':
        return 'person';
      default:
        return 'information-circle';
    }
  }

  getTimelineColor(eventType: string): string {
    switch (eventType) {
      case 'created':
        return 'danger';
      case 'acknowledged':
        return 'primary';
      case 'resolved':
        return 'success';
      case 'assigned':
        return 'warning';
      default:
        return 'medium';
    }
  }

  trackByEvent(index: number, event: TimelineEvent): string {
    return `${event.type}-${event.timestamp.getTime()}`;
  }

  async acknowledgeEscalation() {
    await this.updateEscalation({
      status: ESCALATION_STATUSES.ACKNOWLEDGED,
      assignedToUserId: this.authService.userId,
      assignedToGroupId: null,
    });
  }

  async resolveEscalation() {
    await this.updateEscalation({ status: ESCALATION_STATUSES.RESOLVED });
  }

  async reopenEscalation() {
    await this.updateEscalation({ status: ESCALATION_STATUSES.PENDING });
  }
}
